import {
  DirectionalLight,
  HemisphereLight,
  Mesh,
  PCFSoftShadowMap,
  PMREMGenerator,
  PlaneGeometry,
  ShadowMaterial,
  type Object3D,
  type Scene,
  type Texture,
  type WebGLRenderer,
} from 'three'
import {RoomEnvironment} from 'three/addons/environments/RoomEnvironment.js'

import type {EnvironmentMode} from '../content/manifest.types'
import {
  DIR_LIGHT_INTENSITY,
  ENVIRONMENT_INTENSITY,
  HEMI_LIGHT_INTENSITY,
  SHADOW_BIAS,
  SHADOW_FRUSTUM_M,
  SHADOW_MAP_SIZE,
  SHADOW_NORMAL_BIAS,
  SHADOW_OPACITY,
} from '../config'

export interface Lighting {
  /**
   * Keeps the shadow frustum centred on the placed object. A fixed frustum at
   * the world origin clips the shadow as soon as the user walks a few metres,
   * which reads as "the shadow disappeared".
   */
  followTarget(target: Object3D): void
  dispose(): void
}

/**
 * Lights, shadows and (optionally) an image-based environment.
 *
 * For a PBR model the environment map is not polish: any material with
 * `metalness > 0` renders black without one, and a black hero model over a
 * camera feed is the classic "why does it look broken" report.
 *
 * It is useless for some models though - an emission-only export has a black
 * albedo, so its diffuse term is zero and, being fully metallic, its specular
 * F0 is zero too. Nothing samples the environment, and building the PMREM
 * costs a render target and texture memory for no visible change. Hence
 * `environment: 'none'`, driven from the manifest.
 *
 * The DirectionalLight is always created regardless: it is what casts the
 * contact shadow, and for a flat-shaded model that shadow is the only cue that
 * the object is standing on the floor rather than pasted onto the screen.
 */
export const setupLighting = (
  scene: Scene,
  renderer: WebGLRenderer,
  environment: EnvironmentMode = 'room'
): Lighting => {
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = PCFSoftShadowMap
  // outputColorSpace already defaults to SRGBColorSpace in r185 - do not set it.
  // Tone mapping is left at NoToneMapping so the model's brightness matches the
  // untouched camera feed behind it.

  const hemi = new HemisphereLight(0xffffff, 0x606070, HEMI_LIGHT_INTENSITY)
  scene.add(hemi)

  const sun = new DirectionalLight(0xffffff, DIR_LIGHT_INTENSITY)
  sun.position.set(2, 5, 3)
  sun.castShadow = true
  sun.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE)
  sun.shadow.camera.left = -SHADOW_FRUSTUM_M
  sun.shadow.camera.right = SHADOW_FRUSTUM_M
  sun.shadow.camera.top = SHADOW_FRUSTUM_M
  sun.shadow.camera.bottom = -SHADOW_FRUSTUM_M
  sun.shadow.camera.near = 0.1
  sun.shadow.camera.far = 30
  sun.shadow.bias = SHADOW_BIAS
  sun.shadow.normalBias = SHADOW_NORMAL_BIAS
  scene.add(sun)
  // The target defaults to (0,0,0) but must be in the graph to be updated.
  scene.add(sun.target)

  let envTexture: Texture | null = null
  if (environment === 'room') {
    const pmrem = new PMREMGenerator(renderer)
    const room = new RoomEnvironment()
    envTexture = pmrem.fromScene(room, 0.04).texture
    scene.environment = envTexture
    scene.environmentIntensity = ENVIRONMENT_INTENSITY
    room.dispose()
    pmrem.dispose()
  }

  const lightOffset = sun.position.clone()

  return {
    followTarget: (target) => {
      sun.position.copy(target.position).add(lightOffset)
      sun.target.position.copy(target.position)
      sun.target.updateMatrixWorld()
      sun.shadow.camera.updateProjectionMatrix()
    },
    dispose: () => {
      envTexture?.dispose()
      scene.environment = null
      scene.remove(hemi, sun, sun.target)
      hemi.dispose()
      sun.dispose()
    },
  }
}

export type ShadowCatcher = Mesh<PlaneGeometry, ShadowMaterial>

/**
 * A shadow-receiving disc parented to the placed object. Because it moves with
 * the model, the contact shadow is always exactly under its feet - it cannot
 * drift or float the way a world-fixed plane can when the placement height and
 * the shadow plane height disagree.
 */
export const createShadowCatcher = (sizeMeters: number): ShadowCatcher => {
  const mesh = new Mesh(
    new PlaneGeometry(sizeMeters, sizeMeters),
    new ShadowMaterial({opacity: SHADOW_OPACITY})
  )
  mesh.name = 'shadow-catcher'
  mesh.rotation.x = -Math.PI / 2
  // Lifted a hair to avoid z-fighting with anything else sitting at y = 0.
  mesh.position.y = 0.001
  mesh.receiveShadow = true
  return mesh
}
